import { z } from 'zod'
import {
  COMPLEXITY_MAX,
  COMPLEXITY_MIN,
  INNOVATION_STATUS,
  LEAVE_STATUS,
  LEAVE_TYPE,
  RECURRENCE_CADENCE,
  REVIEW_OUTCOME,
  ROLE,
  SUBMISSION_CHANNEL,
  TASK_PRIORITY,
  TASK_STATUS,
} from './domain.js'

/** Zod 4: z.email() and z.iso.datetime() are top-level, not string methods. */
const email = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim().toLowerCase() : value),
  z.email(),
)
const password = z.string().min(12).max(200)
const isoDateTime = z.iso.datetime({ offset: true })
const shortText = (max: number) => z.string().trim().min(1).max(max)
const id = z.string().min(1).max(64)

const enumOf = <T extends Record<string, string>>(e: T) =>
  z.enum(Object.values(e) as [string, ...string[]])

// ── auth ────────────────────────────────────────────────────────────────────────

export const registerOrganisationSchema = z
  .object({
    organisationName: shortText(120),
    name: shortText(120),
    email,
    password,
  })
  .strict()
export type RegisterOrganisationInput = z.infer<typeof registerOrganisationSchema>

export const loginSchema = z
  .object({ email, password, remember: z.boolean().optional().default(false) })
  .strict()
export type LoginInput = z.infer<typeof loginSchema>

export const forgotPasswordSchema = z.object({ email }).strict()
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>

export const resetPasswordSchema = z
  .object({ token: shortText(200), password })
  .strict()
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>

// ── invites ─────────────────────────────────────────────────────────────────────

export const createInviteSchema = z
  .object({
    email,
    role: enumOf(ROLE).default(ROLE.EMPLOYEE),
    departmentId: id.optional(),
    managerId: id.optional(),
    jobTitle: z.string().trim().max(120).optional(),
  })
  .strict()
export type CreateInviteInput = z.infer<typeof createInviteSchema>

/**
 * 500 is a deliberate ceiling on a single CSV import: it keeps one request bounded and
 * is well above the 100-seat Growth plan, so only Enterprise ever splits an import.
 */
export const bulkInviteSchema = z
  .object({ invites: z.array(createInviteSchema).min(1).max(500) })
  .strict()
export type BulkInviteInput = z.infer<typeof bulkInviteSchema>

export const acceptInviteSchema = z
  .object({ token: shortText(200), password, name: shortText(120).optional() })
  .strict()
export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>

// ── org ─────────────────────────────────────────────────────────────────────────

export const createDepartmentSchema = z.object({ name: shortText(80) }).strict()
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>

export const updateOrganisationSchema = z.object({ name: shortText(120) }).strict()
export type UpdateOrganisationInput = z.infer<typeof updateOrganisationSchema>

// ── tasks ───────────────────────────────────────────────────────────────────────

export const createTaskSchema = z
  .object({
    title: shortText(200),
    description: z.string().trim().max(5000),
    category: shortText(80),
    assigneeId: id,
    priority: enumOf(TASK_PRIORITY),
    complexity: z.number().int().min(COMPLEXITY_MIN).max(COMPLEXITY_MAX),
    deadlineAt: isoDateTime,
    departmentId: id.optional(),
    dependsOnTaskId: id.nullish(),
  })
  .strict()
export type CreateTaskInput = z.infer<typeof createTaskSchema>

/** Manager-side edits that are not status transitions. A reason is required to move a deadline. */
export const updateTaskSchema = z
  .object({
    complexity: z.number().int().min(COMPLEXITY_MIN).max(COMPLEXITY_MAX).optional(),
    assigneeId: id.optional(),
    deadlineAt: isoDateTime.optional(),
    deadlineReason: shortText(500).optional(),
    dependsOnTaskId: id.nullish(),
  })
  .strict()
  .refine((v) => v.deadlineAt === undefined || v.deadlineReason !== undefined, {
    message: 'deadlineReason is required when changing the deadline',
    path: ['deadlineReason'],
  })
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>

export const submitTaskSchema = z
  .object({
    channel: enumOf(SUBMISSION_CHANNEL),
    note: z.string().trim().max(2000).optional(),
    attachmentIds: z.array(id).max(20).optional(),
  })
  .strict()
export type SubmitTaskInput = z.infer<typeof submitTaskSchema>

/** Returning a task without saying why is useless to the employee, so the note is required. */
export const reviewTaskSchema = z
  .object({
    outcome: enumOf(REVIEW_OUTCOME),
    reviewNote: z.string().trim().max(2000).optional(),
    newDeadlineAt: isoDateTime.optional(),
  })
  .strict()
  .refine((v) => v.outcome !== REVIEW_OUTCOME.RETURNED || (v.reviewNote?.length ?? 0) > 0, {
    message: 'reviewNote is required when returning a task',
    path: ['reviewNote'],
  })
export type ReviewTaskInput = z.infer<typeof reviewTaskSchema>

export const blockTaskSchema = z.object({ note: shortText(2000) }).strict()
export type BlockTaskInput = z.infer<typeof blockTaskSchema>

export const externalDelaySchema = z
  .object({ startsAt: isoDateTime, endsAt: isoDateTime, reason: shortText(500) })
  .strict()
  .refine((v) => new Date(v.endsAt) > new Date(v.startsAt), {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
  })
export type ExternalDelayInput = z.infer<typeof externalDelaySchema>

// ── leave ──────────────────────────────────────────────────────────────────────

const ymdDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')

export const createLeaveSchema = z
  .object({
    type: enumOf(LEAVE_TYPE),
    startDate: ymdDate,
    endDate: ymdDate,
    note: shortText(1000).optional(),
  })
  .strict()
  .refine((v) => v.endDate >= v.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  })
export type CreateLeaveInput = z.infer<typeof createLeaveSchema>

export const reviewLeaveSchema = z
  .object({
    decision: z.enum([LEAVE_STATUS.APPROVED, LEAVE_STATUS.DECLINED]),
    decisionNote: shortText(1000).optional(),
    extendTaskIds: z.array(id).optional(),
  })
  .strict()
export type ReviewLeaveInput = z.infer<typeof reviewLeaveSchema>

/**
 * Query strings arrive as text, so page and pageSize are coerced. `status` accepts only
 * stored statuses — OVERDUE is derived and has its own boolean parameter.
 *
 * Deliberately NOT .strict(), unlike every request body above. A body carrying an unknown
 * key is a caller bug worth rejecting. A query string is decorated by the outside world —
 * cache busters, utm_ tags, a proxy, a shared link — and 400ing the whole task list
 * because something appended `_t=123` is disproportionate. Zod's default object behaviour
 * strips unknown keys, which is exactly what a query string wants.
 */
export const taskListQuerySchema = z
  .object({
    status: enumOf(TASK_STATUS).optional(),
    /**
     * Comma-separated list. `status` takes a single value, which cannot express "the
     * active three" — the shape My Day needs. Filtering client-side instead would leave
     * the paging bug: closed tasks still consume the page-size budget.
     */
    statuses: z
      .string()
      .trim()
      .min(1)
      .transform((raw) => raw.split(','))
      .pipe(z.array(enumOf(TASK_STATUS)).min(1).max(6))
      .optional(),
    overdue: z.stringbool().optional(),
    assigneeId: id.optional(),
    departmentId: id.optional(),
    priority: enumOf(TASK_PRIORITY).optional(),
    complexityMin: z.coerce.number().int().min(1).max(10).optional(),
    complexityMax: z.coerce.number().int().min(1).max(10).optional(),
    q: z.string().trim().max(200).optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .refine((v) => v.status === undefined || v.statuses === undefined, {
    message: 'Send status or statuses, not both',
    path: ['statuses'],
  })
export type TaskListQuery = z.infer<typeof taskListQuerySchema>

// ── analytics ──────────────────────────────────────────────────────────────────

const analyticsPeriodSchema = z
  .object({
    from: isoDateTime,
    to: isoDateTime,
    departmentId: id.optional(),
  })
  .strict()

export const analyticsSummaryQuerySchema = analyticsPeriodSchema
export const analyticsPeopleQuerySchema = analyticsPeriodSchema
export const analyticsThroughputQuerySchema = z
  .object({
    from: isoDateTime,
    to: isoDateTime,
    departmentId: id.optional(),
    bucket: z.enum(['week', 'month']).default('week'),
  })
  .strict()
export const analyticsInsightQuerySchema = z
  .object({ from: isoDateTime, to: isoDateTime, departmentId: id.optional() })
  .strict()
export const analyticsDepartmentsQuerySchema = z
  .object({ from: isoDateTime, to: isoDateTime, departmentId: id.optional() })
  .strict()
export const analyticsManagersQuerySchema = z.object({ from: isoDateTime, to: isoDateTime }).strict()
export const analyticsTrendQuerySchema = z
  .object({
    from: isoDateTime,
    to: isoDateTime,
    departmentId: id.optional(),
    assigneeId: id.optional(),
    bucket: z.enum(['week', 'month']).default('week'),
  })
  .strict()
export const activityQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict()
export type ActivityQuery = z.infer<typeof activityQuerySchema>

// ── task templates ──────────────────────────────────────────────────────────────

const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (24-hour)')

/**
 * Spec M3b §2.1 invariants, as data so the PATCH route can re-check them against
 * the MERGED row — a partial edit must not be able to leave a series invalid.
 * Returns a field-keyed issue map; empty means valid.
 */
export function templateScheduleIssues(t: {
  cadence?: string | null
  weekday?: number | null
  dayOfMonth?: number | null
  dueTime?: string | null
  assigneeId?: string | null
}): Record<string, string> {
  const issues: Record<string, string> = {}
  if (t.cadence == null) {
    if (t.weekday != null) issues['weekday'] = 'weekday requires a WEEKLY cadence'
    if (t.dayOfMonth != null) issues['dayOfMonth'] = 'dayOfMonth requires a MONTHLY cadence'
    if (t.dueTime != null) issues['dueTime'] = 'dueTime requires a cadence'
    if (t.assigneeId != null) issues['assigneeId'] = 'assigneeId requires a cadence'
    return issues
  }
  if (t.dueTime == null) issues['dueTime'] = 'A scheduled template needs a due time'
  if (t.assigneeId == null) issues['assigneeId'] = 'A scheduled template needs an assignee'
  if (t.cadence === RECURRENCE_CADENCE.WEEKLY && t.weekday == null)
    issues['weekday'] = 'A weekly series needs a weekday'
  if (t.cadence !== RECURRENCE_CADENCE.WEEKLY && t.weekday != null)
    issues['weekday'] = 'weekday requires a WEEKLY cadence'
  if (t.cadence === RECURRENCE_CADENCE.MONTHLY && t.dayOfMonth == null)
    issues['dayOfMonth'] = 'A monthly series needs a day of month'
  if (t.cadence !== RECURRENCE_CADENCE.MONTHLY && t.dayOfMonth != null)
    issues['dayOfMonth'] = 'dayOfMonth requires a MONTHLY cadence'
  return issues
}

const templateShape = {
  name: shortText(80),
  title: shortText(200),
  description: z.string().trim().max(5000).default(''),
  category: shortText(80),
  priority: enumOf(TASK_PRIORITY),
  complexity: z.number().int().min(COMPLEXITY_MIN).max(COMPLEXITY_MAX),
  defaultAssigneeId: id.nullish(),
  cadence: enumOf(RECURRENCE_CADENCE).nullish(),
  weekday: z.number().int().min(1).max(7).nullish(),
  dayOfMonth: z.number().int().min(1).max(31).nullish(),
  dueTime: timeOfDay.nullish(),
  assigneeId: id.nullish(),
  isPaused: z.boolean().default(false),
}

export const createTemplateSchema = z
  .object(templateShape)
  .strict()
  .superRefine((value, ctx) => {
    for (const [path, message] of Object.entries(templateScheduleIssues(value))) {
      ctx.addIssue({ code: 'custom', path: [path], message })
    }
  })
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>

// ── innovation ──────────────────────────────────────────────────────────────────

export const createPromptSchema = z
  .object({
    question: shortText(500),
    opensAt: isoDateTime,
    closesAt: isoDateTime,
  })
  .strict()
  .refine((v) => new Date(v.closesAt) > new Date(v.opensAt), {
    message: 'closesAt must be after opensAt',
    path: ['closesAt'],
  })
export type CreatePromptInput = z.infer<typeof createPromptSchema>

/**
 * Partial, so a manager can fix a typo without resending the window. The
 * ordering rule is re-checked in the route against the MERGED row — a partial
 * edit must not be able to leave closesAt before opensAt.
 */
export const patchPromptSchema = z
  .object({
    question: shortText(500).optional(),
    opensAt: isoDateTime.optional(),
    closesAt: isoDateTime.optional(),
  })
  .strict()
export type PatchPromptInput = z.infer<typeof patchPromptSchema>

export const createSubmissionSchema = z
  .object({ promptId: id, body: shortText(5000) })
  .strict()
export type CreateSubmissionInput = z.infer<typeof createSubmissionSchema>

/** PRD §8.2: a review lands on one of the two terminal verdicts. */
export const reviewSubmissionSchema = z
  .object({
    status: z.enum([INNOVATION_STATUS.ADOPTED, INNOVATION_STATUS.NOT_PURSUED]),
    managerNote: z.string().trim().max(2000).optional(),
  })
  .strict()
export type ReviewSubmissionInput = z.infer<typeof reviewSubmissionSchema>

/** Merged-row invariants are checked in the route, not here (see above). */
export const patchTemplateSchema = z.object(templateShape).partial().strict()
export type PatchTemplateInput = z.infer<typeof patchTemplateSchema>
